import React, { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import StyledLink from 'components/generic/typography/StyledLink'
import DataGridWithSearch from 'components/generic/datagrid/DataGridWithSearch'
import { setLoading } from 'state/loading'
import { setDecimalPrecision } from 'utils/helpers'
import { generateRenderHeaderFunction } from 'utils/generateRenderHeaderFunction'
import { SORTABLE_BILL_FIELDS } from 'utils/constants'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'

const BillsGrid = props => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { enableBatchBills } = useSelector(state => state.featureFlags?.variables || {})

  const { filter, setFilter } = props
  const renderHeaderWithSort = useMemo(() => generateRenderHeaderFunction(filter, setFilter), [filter])

  const handleNavigation = params => {
    dispatch(setLoading(true))
    setTimeout(() => {
      navigate(params.row.billLink)
    }, 0)
  }

  const columns = [
    {
      field: 'billNumber',
      headerName: 'Bill #',
      flex: 1,
      renderCell: params => (
        <StyledLink
          to={params.row.billLink}
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            if (event.metaKey) {
              window.open(params.row.billLink, '_blank')
            } else {
              handleNavigation(params)
            }
          }}
        >
          {params.row.billNumber}
        </StyledLink>
      ),
      renderHeader: () => renderHeaderWithSort('Bill #', SORTABLE_BILL_FIELDS.EXTERNAL_ID, { startAscending: false }),
      sortable: false,
    },
    {
      field: 'billStatus',
      headerName: 'Status',
      flex: 1,
      renderHeader: () => renderHeaderWithSort('Status', SORTABLE_BILL_FIELDS.STATUS),
      sortable: false,
    },
    {
      field: 'ticketCount',
      headerName: '# of Tickets',
      headerAlign: 'right',
      align: 'right',
      flex: 1,
      renderHeader: () =>
        renderHeaderWithSort('# of Tickets', SORTABLE_BILL_FIELDS.NUMBER_OF_TICKETS, {
          align: 'right',
          startAscending: false,
        }),
      sortable: false,
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      flex: 2,
      renderCell: ({ row }) => {
        const { customerName, customerId } = row
        return customerId && customerName ? (
          <Link to={`/app/customers/${customerId}/account`} target="_blank">
            {customerName}
          </Link>
        ) : null
      },
      renderHeader: () =>
        renderHeaderWithSort('Customer', SORTABLE_BILL_FIELDS.CUSTOMER_DISPLAY_NAME, { startAscending: true }),
      sortable: false,
    },
    {
      field: 'customerPreferredPaymentMethod',
      headerName: 'Preferred Payment Method',
      flex: 2,
      sortable: false,
    },
    {
      field: 'billTotal',
      headerName: 'Bill Total',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) =>
        `${row?.pricePrepend || '$'}${setDecimalPrecision(row?.billTotal, { decimalPrecisionOverride: 2 })}`,
      sortable: false,
    },
    {
      field: 'paid',
      headerName: 'Paid',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) =>
        `${row?.pricePrepend || '$'}${setDecimalPrecision(row?.paid, { decimalPrecisionOverride: 2 })}`,
      sortable: false,
    },
    {
      field: 'lastUpdated',
      headerName: 'Last Updated',
      type: 'date',
      flex: 1,
      renderCell: ({ row }) => (row?.lastUpdated ? dateFormatter(row?.lastUpdated) : undefined),
      renderHeader: () =>
        renderHeaderWithSort('Last Updated', SORTABLE_BILL_FIELDS.LAST_UPDATED, { startAscending: false }),
      sortable: false,
    },
  ]

  const filteredColumns = columns.filter(column => {
    if (column.field === 'customerPreferredPaymentMethod' && !enableBatchBills) return false
    return true
  })

  return <DataGridWithSearch {...props} columns={filteredColumns} useBackendSortAndFilter />
}

BillsGrid.propTypes = DataGridWithSearch.propTypes

BillsGrid.defaultProps = DataGridWithSearch.defaultProps

export default BillsGrid
